import { useForm, useWatch } from 'react-hook-form';
import { ThemeButton } from '@shared/ui/buttons';
import { DialogContent, DialogActions } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import Note from '@shared/ui/messages/Note';
import { useGroups } from '@app/api/groups/useGroups';
import { useTranslation } from 'react-i18next';
import { getDirtyValues, getTotalCards } from '@utils/helpers';
import { validateServerErrors } from '@utils/validation';
import { roles } from '@shared/static/roles';
import {
  TeamNameInput,
  TeamleadPicker,
  AvatarPicker,
  AvatarColorPicker,
  CardsActionPicker,
  BasicSelectInput,
  MemberRoleView,
} from '@shared/ui/forms/inputs';

export default function CreateTeamDialog({
  close,
  data,
  setTitle,
  setImage,
  setSubtitle,
  setHideCloseButton,
}) {
  const { t } = useTranslation();

  const [loading, setLoading] = useState(false);
  const [isCardsScreen, setIsCardsScreen] = useState(false);
  const [activeTeamLead, setActiveTeamLead] = useState(null);
  const [availableTeamLeads, setAvailableTeamLeads] = useState([]);

  const { createGroup, updateGroup, fetchAvailableTeamLeads } = useGroups();

  const {
    control,
    handleSubmit,
    setError,
    formState: { errors, dirtyFields, isDirty },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      name: data?.name || '',
      avatar: {
        name: data?.avatar?.name || 'google',
        color: data?.avatar?.color || '#4D72ED',
      },
      teamLead_id: data?.teamLead?.id || null,
      teamlead_transfer: {
        new_teamlead_user_id: data?.teamLead?.id || null,
        cards_action: null,
        cards_transfer_user_id: null,
      },
    },
  });

  const teamleadTransferWatch = useWatch({
    control,
    name: 'teamlead_transfer',
  });

  const activeTeamLeadCardsCount = getTotalCards(activeTeamLead);

  const needCardsAction =
    teamleadTransferWatch?.new_teamlead_user_id !== activeTeamLead?.id &&
    activeTeamLeadCardsCount > 0;

  const buttonText = !data
    ? t('create')
    : needCardsAction
      ? t('continue')
      : t('save');

  const users = data?.users ?? [];

  const availableUsers = users.filter((x) => x.id !== activeTeamLead?.id);

  const buildTeamleadTransfer = useCallback(
    (payload, formData) => {
      if (!payload.teamlead_transfer) return payload;

      const { cards_action, new_teamlead_user_id } = payload.teamlead_transfer;
      const hasCards = activeTeamLeadCardsCount > 0;

      const teamlead_transfer = {
        new_teamlead_user_id,
        ...(hasCards && { cards_action }),
        ...(cards_action === 'transfer' && {
          cards_transfer_user_id:
            formData.teamlead_transfer.cards_transfer_user_id,
        }),
      };

      return { ...payload, teamlead_transfer };
    },
    [activeTeamLeadCardsCount],
  );

  const handleCreate = useCallback(
    async (formData) => {
      try {
        // eslint-disable-next-line no-unused-vars
        const { teamlead_transfer: _teamlead_transfer, ...createData } =
          formData;

        setLoading(true);
        const res = await createGroup(createData);
        if (res) close();
      } catch (err) {
        validateServerErrors(setError, err);
      } finally {
        setLoading(false);
      }
    },
    [createGroup, close, setError],
  );

  const handleUpdate = useCallback(
    async (formData) => {
      if (!data?.id) return;

      try {
        setLoading(true);
        let payload = getDirtyValues(dirtyFields, formData);
        payload = buildTeamleadTransfer(payload, formData);

        const res = await updateGroup(
          {
            group: {
              ...payload,
              id: data.id,
            },
          },
          data,
        );

        if (res) close();
      } catch (err) {
        validateServerErrors(setError, err);
      } finally {
        setLoading(false);
      }
    },
    [data, dirtyFields, buildTeamleadTransfer, updateGroup, close, setError],
  );

  const onSubmit = useCallback(
    async (formData) => {
      if (!data?.name) {
        await handleCreate(formData);
      } else {
        await handleUpdate(formData);
      }
    },
    [data?.name, handleCreate, handleUpdate],
  );

  useEffect(() => {
    const fetchData = async () => {
      const leads = await fetchAvailableTeamLeads();
      if (leads) setAvailableTeamLeads(leads);
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isCardsScreen) {
      setTitle('team_lead_cards');
      setImage('cards');
      setHideCloseButton(true);
      setSubtitle('');
    } else {
      if (data?.name) {
        setTitle(data.name);
        setSubtitle(t('team'));
        setActiveTeamLead(data.teamLead ?? null);
        setImage('');
        setHideCloseButton(false);
      } else {
        setTitle('new_team');
        setImage('');
        setHideCloseButton(false);
        setSubtitle('');
      }
    }
  }, [
    t,
    isCardsScreen,
    data?.name,
    setTitle,
    setImage,
    setHideCloseButton,
    setSubtitle,
    setActiveTeamLead,
  ]);

  return (
    <>
      <DialogContent>
        {isCardsScreen ? (
          <>
            <div className="form">
              <p>{t('what_to_do_team_lead_cards')}</p>

              {activeTeamLead && (
                <MemberRoleView
                  member={{ ...activeTeamLead, group_role: roles.TEAM_LEAD }}
                />
              )}

              <CardsActionPicker
                error={errors.teamlead_transfer?.cards_action}
                control={control}
                type="editTeam"
                name="teamlead_transfer.cards_action"
              />

              {teamleadTransferWatch?.cards_action === 'transfer' && (
                <BasicSelectInput
                  control={control}
                  name="teamlead_transfer.cards_transfer_user_id"
                  label={t('new_owner')}
                  icon="user-input"
                  placeholder={t('Administrator')}
                  searchable
                  data={availableUsers}
                  error={errors.teamlead_transfer?.cards_transfer_user_id}
                />
              )}
            </div>

            <Note
              type="warning"
              marginTop
              title={t('important')}
              content={t('edit_team_cards_note')}
            >
              <ul>
                <li>{t('edit_team_cards_note_1')}</li>
                <li>{t('edit_team_cards_note_2')}</li>
              </ul>
            </Note>
          </>
        ) : (
          <div className="form create-team-form">
            <TeamNameInput control={control} errors={errors} />
            <TeamleadPicker
              name={
                data ? 'teamlead_transfer.new_teamlead_user_id' : 'teamLead_id'
              }
              currentTeamLead={data?.teamLead}
              control={control}
              errors={errors}
              teamLeads={availableTeamLeads}
            />
            <AvatarPicker control={control} />
            <AvatarColorPicker control={control} />
          </div>
        )}
      </DialogContent>

      <DialogActions>
        {isCardsScreen ? (
          <div className="btns-toolbar">
            <ThemeButton
              onClick={handleSubmit(onSubmit)}
              startIcon="check"
              loading={loading}
            >
              {t('confirm')}
            </ThemeButton>
            <ThemeButton
              onClick={() => setIsCardsScreen(false)}
              appearance="secondary"
            >
              {t('cancel')}
            </ThemeButton>
          </div>
        ) : (
          <ThemeButton
            onClick={
              needCardsAction
                ? () => setIsCardsScreen(true)
                : handleSubmit(onSubmit)
            }
            startIcon="check"
            loading={loading}
            disabled={data && !isDirty}
            block={false}
            minWidth
          >
            {buttonText}
          </ThemeButton>
        )}
      </DialogActions>
    </>
  );
}
